import type { ComponentType } from 'react';
import type { ValueToLabelCache } from '@/hooks/useValueToLabelCache';

/**
 * コンボボックスの選択タイプを定義する定数
 */
export const SelectType = {
  /** 単一選択 */
  single: 'single',
  /** 複数選択 */
  multiple: 'multiple'
} as const;
export type SelectType = (typeof SelectType)[keyof typeof SelectType];
export const SelectionDisplayStyle = {
  chip: 'chip',
  hidden: 'hidden'
} as const;
export type SelectionDisplayStyle = (typeof SelectionDisplayStyle)[keyof typeof SelectionDisplayStyle];

/**
 * 選択タイプの基本プロパティ
 */
type BaseSelectProps<T extends SelectType> = {
  /** 選択タイプ（single または multiple） */
  type: T;
};

/**
 * 単一選択用のプロパティ
 */
type SingleSelectProps = BaseSelectProps<typeof SelectType.single> & {
  /** 選択された値（単一） */
  value: string | null;
  /** 値が変更された時のコールバック */
  onValueChange: (value: string | null) => void;
};

/**
 * 複数選択用のプロパティ
 */
type MultiSelectProps = BaseSelectProps<typeof SelectType.multiple> & {
  /** 選択された値（複数） */
  value: string[];
  /** 値が変更された時のコールバック */
  onValueChange: (value: string[]) => void;
};

/**
 * コンボボックスのオプション項目の基本型
 */
export interface ComboboxOption {
  /** オプションの一意キー（省略可） */
  key?: string;
  /** 表示ラベル */
  label: string;
  /** 値 */
  value: string;
  /** メールアドレス */
  email?: string;
  /** 無効状態か */
  disabled?: boolean;
  /** アバターURL（オプション） */
  avatarUrl?: string | null;
  /** label 以外に検索対象としたいキーワード（かな、ローマ字等） */
  searchKeywords?: string[];
}

/**
 * オプション項目のレンダリングに使用されるプロパティ
 */
export interface ComboboxOptionRenderProps<TOption extends ComboboxOption> {
  /** オプション項目 */
  option: TOption;
  /** 選択状態か */
  isSelected: boolean;
  /** チェックボックスを表示するか */
  showCheckbox?: boolean;
}

/**
 * オプション項目をレンダリングするコンポーネントの型
 */
export type ComboboxOptionComponent<TOption extends ComboboxOption> = ComponentType<ComboboxOptionRenderProps<TOption>>;

/**
 * オプション項目をレンダリングする関数の型
 */
export type ComboboxOptionRenderer<TOption extends ComboboxOption> = (props: ComboboxOptionRenderProps<TOption>) => React.ReactNode;

/**
 * コンボボックスの基本プロパティ
 */
interface ComboboxBaseProps<TOption extends ComboboxOption> {
  /** 選択可能なオプション一覧 */
  options: readonly TOption[];
  /** 検索文字列 */
  search?: string;
  /** 検索文字列が変更された時のコールバック */
  onSearchChange?: (search: string) => void;
  /** 検索入力欄を非表示にするか */
  hideSearch?: boolean;
  /** ローディング中か */
  isPending?: boolean;
  /** プレースホルダーデータを表示中か */
  isPlaceholderData?: boolean;
  /** フェッチ中か */
  isFetching?: boolean;
  /** 検索入力欄のプレースホルダー */
  searchPlaceholder?: string;
  /** 検索結果が0件の場合のメッセージ */
  noResultsMessage?: string;
  /** チェックボックスを表示するか */
  showCheckbox?: boolean;
  /** スクロール末尾到達時のコールバック */
  onScrollToEnd?: () => void;
  /** オプション項目のカスタムレンダリング */
  renderOption?: ComboboxOptionRenderer<TOption> | ComboboxOptionComponent<TOption>;
  /** 値とラベルのキャッシュ */
  valueToLabelCache?: ValueToLabelCache;
  /** 選択表示スタイル */
  selectionDisplayStyle?: SelectionDisplayStyle;
}

/**
 * コンボボックスのプロパティ
 */
export type ComboboxProps<TOption extends ComboboxOption> = ComboboxBaseProps<TOption> & (SingleSelectProps | MultiSelectProps);